import { NgModule } from "@angular/core";
import { Routes, RouterModule } from "@angular/router";
import { AuthGuard } from "./guard/auth.guard";

// Import Containers
import { DefaultLayoutComponent, EmailLayoutComponent } from "./containers";
import { P404Component } from "./views/error/404.component";
import { P500Component } from "./views/error/500.component";
import { LoginComponent } from "./views/login/login.component";
import { RegisterComponent } from "./views/register/register.component";

export const routes: Routes = [
  // ✅ ROOT REDIRECT – NO GUARD HERE
  {
    path: "",
    redirectTo: "dashboard",
    pathMatch: "full",
  },
  {
    path: "404",
    component: P404Component,
    data: { title: "Page 404" },
  },
  {
    path: "500",
    component: P500Component,
    data: { title: "Page 500" },
  },
  {
    path: "login",
    component: LoginComponent,
    data: { title: "Login Page" },
  },
  {
    path: "register",
    component: RegisterComponent,
    data: { title: "Register Page" },
  },

  // EMAIL (public or guarded as you wish)
  {
    path: "apps/email",
    component: EmailLayoutComponent,
    children: [
      {
        path: "",
        loadChildren: () =>
          import("./views/apps/email/email.module").then((m) => m.EmailModule),
      },
    ],
  },

  // ✅ MAIN LAYOUT – ALL PROTECTED ROUTES
  {
    path: "",
    component: DefaultLayoutComponent,
    data: { title: "Home" },
    canActivate: [AuthGuard], // ✅ ONLY HERE
    children: [
      // Dashboard
      {
        path: "dashboard",
        loadChildren: () =>
          import("./views/dashboard/dashboard.module").then(
            (m) => m.DashboardModule,
          ),
      },

      // Bank Reports
      {
        path: "bankreports",
        loadChildren: () =>
          import("./views/inventory/BankReports/bankreport.module").then(
            (m) => m.BankreportModule,
          ),
      },

      // Milk Transaction Report
      {
        path: "milkTransactionreport",
        loadChildren: () =>
          import(
            "./views/inventory/milkTransactionReport/milkTransactionReport.module"
          ).then((m) => m.MilkTransactionReportModule),
      },

      // Producer Registration
      {
        path: "producer-registration",
        loadChildren: () =>
          import(
            "./views/inventory/ProducerDetails/producerDetails.module"
          ).then((m) => m.ProducerDetailsModule),
      },

      // Old URLs → redirect into corresponding tabs
      {
        path: "producer-bank-details",
        redirectTo: "producer-registration/bank-details",
        pathMatch: "full",
      },
      {
        path: "producer-services-registration",
        redirectTo: "producer-registration/service-registration",
        pathMatch: "full",
      },

      // Society Registration
      {
        path: "society-registration",
        loadChildren: () =>
          import(
            "./views/inventory/SociteyRegistration/societyRegistration.module"
          ).then((m) => m.SocietyRegistrationModule),
      },

      // Daily Report
      {
        path: "daily-report",
        loadChildren: () =>
          import("./views/inventory/dailyReport/dailyReport.module").then(
            (m) => m.DailyReportModule,
          ),
      },
      {
        path: "dailyreport",
        redirectTo: "daily-report",
        pathMatch: "full",
      },

      // ✅ DAIRY INVOICE ENTRY - NEW ROUTE
      {
        path: "inventory/dairy-invoice-entry",
        loadChildren: () =>
          import(
            "./views/inventory/dairy-invoice-entry/dairy-invoice-entry.module"
          ).then((m) => m.DairyInvoiceEntryModule),
      },

      // Sales Report
      {
        path: "salesreport",
        loadChildren: () =>
          import("./views/inventory/salesReport/salesReport.module").then(
            (m) => m.salesreportModule,
          ),
      },

      // Payout Report
      {
        path: "payoutreport",
        loadChildren: () =>
          import("./views/inventory/payoutReport/payoutReport.module").then(
            (m) => m.payoutreportModule,
          ),
      },

      // Recoveries Report
      {
        path: "recoveries-report",
        loadChildren: () =>
          import(
            "./views/inventory/RecoveryReport/recoveriesReport.module"
          ).then((m) => m.RecoveriesReportModule),
      },

      {
        path: "addItems",
        loadChildren: () =>
          import("./views/inventory/addItems/addItems.module").then(
            (m) => m.AddItemsInitModule,
          ),
      },
      {
        path: "base",
        loadChildren: () =>
          import("./views/base/base.module").then((m) => m.BaseModule),
      },
      {
        path: "forgotPassword",
        loadChildren: () =>
          import("./views/forgotPassword/forgotPassword.module").then(
            (m) => m.ForgotPasswordModule,
          ),
      },

      // Shift report
      {
        path: "shiftreport",
        loadChildren: () =>
          import("./views/inventory/shiftReport/shiftReport.module").then(
            (m) => m.shiftreportModule,
          ),
      },

      // Statement report
      {
        path: "statementreport",
        loadChildren: () =>
          import(
            "./views/inventory/milkTransactionReport/milkTransactionReport.module"
          ).then((m) => m.MilkTransactionReportModule),
      },
      {
        path: "statement-report",
        redirectTo: "statementreport",
        pathMatch: "full",
      },

      {
        path: "buttons",
        loadChildren: () =>
          import("./views/buttons/buttons.module").then((m) => m.ButtonsModule),
      },
      {
        path: "charts",
        loadChildren: () =>
          import("./views/chartjs/chartjs.module").then((m) => m.ChartjsModule),
      },
      {
        path: "editors",
        loadChildren: () =>
          import("./views/editors/editors.module").then((m) => m.EditorsModule),
      },
      {
        path: "forms",
        loadChildren: () =>
          import("./views/forms/forms.module").then((m) => m.FormsModule),
      },
      {
        path: "google-maps",
        loadChildren: () =>
          import("./views/maps/maps.module").then((m) => m.MapsModule),
      },
      {
        path: "icons",
        loadChildren: () =>
          import("./views/icons/icons.module").then((m) => m.IconsModule),
      },
      {
        path: "notifications",
        loadChildren: () =>
          import("./views/notifications/notifications.module").then(
            (m) => m.NotificationsModule,
          ),
      },
      {
        path: "plugins",
        loadChildren: () =>
          import("./views/plugins/plugins.module").then((m) => m.PluginsModule),
      },

      {
        path: "producer-requirement-indents",
        loadChildren: () =>
          import(
            "./views/inventory/ProducerIndent/producer-requirement-indents.module"
          ).then((m) => m.ProducerRequirementIndentsModule),
      },

      {
        path: "tables",
        loadChildren: () =>
          import("./views/tables/tables.module").then((m) => m.TablesModule),
      },
      {
        path: "widgets",
        loadChildren: () =>
          import("./views/widgets/widgets.module").then((m) => m.WidgetsModule),
      },

      {
        path: "variance-shiftwise",
        loadChildren: () =>
          import(
            "./views/inventory/varianceShiftwiseReport/variance-shiftwise.module"
          ).then((m) => m.VarianceShiftwiseModule),
      },

      {
        path: "producer-reports/day-wise",
        loadChildren: () =>
          import(
            "./views/inventory/ProducerDayWise/producer-daywise.module"
          ).then((m) => m.ProducerDaywiseModule),
      },
      {
        path: "producer-reports/passbook",
        loadChildren: () =>
          import(
            "./views/inventory/ProducerPassbook/producer-passbook.module"
          ).then((m) => m.ProducerPassbookModule),
      },
      {
        path: "producer-reports/bank-report",
        loadChildren: () =>
          import(
            "./views/inventory/ProducerBankReport/producer-bank-report.module"
          ).then((m) => m.ProducerBankReportModule),
      },

      {
        path: "bcc/milk-collection-statement",
        loadChildren: () =>
          import(
            "./views/inventory/ProducerConsolidated/producer-consolidated-report.module"
          ).then((m) => m.ProducerConsolidatedReportModule),
      },

      {
        path: "bcc/daywise-periodic-statement",
        loadChildren: () =>
          import(
            "./views/inventory/DaywisePeriodicStatement/daywise-periodic-statement.module"
          ).then((m) => m.DaywisePeriodicStatementModule),
      },

      {
        path: "producer-reports/bank-report",
        loadChildren: () =>
          import(
            "./views/inventory/ProducerBankReport/producer-bank-report.module"
          ).then((m) => m.ProducerBankReportModule),
      },

      {
        path: "producer-passbook",
        loadChildren: () =>
          import(
            "./views/inventory/ProducerPassbook/producer-passbook.module"
          ).then((m) => m.ProducerPassbookModule),
      },

      {
        path: "agent-reports/bank-report",
        loadChildren: () =>
          import(
            "./views/inventory/AgentBankReport/agent-bank-report.module"
          ).then((m) => m.AgentBankReportModule),
      },

      {
        path: "inventory/AgentConsolidated",
        loadChildren: () =>
          import(
            "./views/inventory/AgentConsolidated/agent-consolidated.module"
          ).then((m) => m.AgentConsolidatedModule),
      },

      {
        path: "agent-reports/day-wise",
        loadChildren: () =>
          import("./views/inventory/AgentDayWise/agent-daywise.module").then(
            (m) => m.AgentDayWiseModule,
          ),
      },

      {
        path: "bcc-registration",
        loadChildren: () =>
          import(
            "./views/inventory/BccRegistration/bccRegistration.module"
          ).then((m) => m.BccRegistrationModule),
      },

      {
        path: "apps",
        loadChildren: () =>
          import("./views/apps/apps.module").then((m) => m.AppsModule),
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: "top",
      anchorScrolling: "enabled",
      relativeLinkResolution: "legacy",
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
